import { ID, NewTask } from '@griply/types';

import { detachWrite } from '../detach-write';
import { FirestoreInstance } from '../types/firestore';
import { FirestoreCollections } from '../types/firestore-collections';
import { buildTaskDocument } from './build-task-document';

export type CreateTasksOptions<
  TDocRef = unknown,
  TCollectionRef = unknown,
  TFieldValue = unknown,
  TTimestamp = unknown,
  TDb = FirestoreInstance,
> = {
  tasks: NewTask[];
  userId: ID;
  collection: (
    db: NoInfer<TDb>,
    path: string,
    ...rest: string[]
  ) => TCollectionRef;
  doc: (
    collectionRef: NoInfer<TCollectionRef>,
    ...rest: string[]
  ) => TDocRef & { id: ID };
  setDoc: (
    ref: NoInfer<TDocRef>,
    data: Record<ID, unknown>,
  ) => Promise<void> | void;
  db: TDb;
  serverTimestamp: () => TFieldValue;
  timestampFromDate: (date: Date) => TTimestamp;
  writeBatch?: (db: NoInfer<TDb>) => {
    set: (ref: NoInfer<TDocRef>, data: Record<ID, unknown>) => void;
    commit: () => Promise<void>;
  };
  onWriteError: (error: unknown) => void;
};

export const createTasks = async <
  TDocRef,
  TCollectionRef,
  TFieldValue,
  TTimestamp,
  TDb = FirestoreInstance,
>(
  options: CreateTasksOptions<
    TDocRef,
    TCollectionRef,
    TFieldValue,
    TTimestamp,
    TDb
  >,
): Promise<ID[]> => {
  const {
    tasks,
    userId,
    collection,
    doc,
    setDoc,
    db,
    serverTimestamp,
    timestampFromDate,
    writeBatch,
    onWriteError,
  } = options;

  const taskCollection = collection(db, FirestoreCollections.Tasks);
  const ids: ID[] = [];

  const writes = tasks.flatMap((task) => {
    const docRef = doc(taskCollection);
    ids.push(docRef.id);
    const data = buildTaskDocument(
      task,
      docRef,
      userId,
      serverTimestamp,
      timestampFromDate,
      task.parentIds ?? null,
    );
    return [{ ref: docRef, data }];
  });

  if (writes.length === 1 || !writeBatch) {
    writes.forEach(({ ref, data }) =>
      detachWrite(setDoc(ref, data), onWriteError),
    );
    return ids;
  }

  const batch = writeBatch(db);
  writes.forEach(({ ref, data }) => batch.set(ref, data));
  detachWrite(batch.commit(), onWriteError);
  return ids;
};
